import { useState, useEffect, useCallback, useRef } from 'react';
import '../../Styles/ArtistRoot.css';

import { useParams, Link, useNavigate } from 'react-router-dom';
import { MdAlbum, MdDelete, MdAdd, MdCloudUpload, MdArrowUpward, MdArrowDownward, MdEdit, MdCheck, MdCheckCircle, MdCancel, MdLock, MdPublic, MdPlayArrow, MdPause } from 'react-icons/md';
import '../../Styles/Admin.css';
import izenuAPI from '../../services/izenuAPI';
import GENRES from '../../utils/Genres';
import ReleaseTrackUploader from './ReleaseTrackUploader';
import UpcQuestion, { upcProblem, upcPayload } from './UpcQuestion';

const TYPE_LABELS = { album: 'Álbum', ep: 'EP', single: 'Single' };
// Lower-case noun for sentences ("este álbum", "este EP"); all three are masculine.
const KIND = { album: 'álbum', ep: 'EP', single: 'single' };
const STATUS = {
    active: ['Activo', 'bg-green-100 text-green-800'],
    draft: ['Rascunho', 'bg-amber-100 text-amber-800'],
    takedown: ['Takedown', 'bg-red-100 text-red-800'],
};
const ARTWORK_MAX_MB = 10;

const formatDuration = (ms) => {
    if (!ms) return '';
    const total = Math.round(Number(ms) / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export default function Release() {
    const [release, setRelease] = useState(null);
    // Track being listened to. The <audio> under its row gives seek and volume for free.
    const [listening, setListening] = useState(null); // music_id
    const [audioPaused, setAudioPaused] = useState(true);
    const audioRef = useRef(null);
    const [showAddTrack, setShowAddTrack] = useState(false);
    const [availableTracks, setAvailableTracks] = useState([]);
    const [loadingTracks, setLoadingTracks] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [artworkFile, setArtworkFile] = useState(null);
    const [artworkPreview, setArtworkPreview] = useState(null);
    const [uploadingArtwork, setUploadingArtwork] = useState(false);

    // Edit states
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editType, setEditType] = useState('');
    const [editGenre, setEditGenre] = useState('');
    const [editUpc, setEditUpc] = useState('');
    const [editUpcMode, setEditUpcMode] = useState('');
    const [editReleaseDate, setEditReleaseDate] = useState('');
    const [editStatus, setEditStatus] = useState('active');
    const [editTakedownDate, setEditTakedownDate] = useState('');
    const [saving, setSaving] = useState(false);

    // Sale states
    const [savingSale, setSavingSale] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [artworkError, setArtworkError] = useState('');

    let { release_id } = useParams();
    const navigate = useNavigate();

    const setCover = useCallback((src, size) => {
        const big = size === '100%' || size > 100;
        if (!src || src === "") {
            return (
                <div style={{ width: size, height: size, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: "#e5e7eb" }}>
                    <MdAlbum size={big ? 56 : 20} color="#9ca3af" />
                </div>
            );
        }
        const types = ['jpg', 'jpeg', 'png', 'webp'];
        const parts = src.split('/');
        const last = parts[parts.length - 1].split('.');
        const ext = last[last.length - 1]?.toLowerCase();
        if (types.indexOf(ext) !== -1) {
            return <img src={src} style={{ width: size, height: size, objectFit: 'cover' }} alt="" />;
        }
        return (
            <div style={{ width: size, height: size, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: "#e5e7eb" }}>
                <MdAlbum size={big ? 56 : 20} color="#9ca3af" />
            </div>
        );
    }, []);

    const trackUrl = (t) => t.music_url || t.preview_url || '';

    const toggleListen = (t) => {
        if (listening === t.music_id && audioRef.current) {
            if (audioRef.current.paused) audioRef.current.play();
            else audioRef.current.pause();
            return;
        }
        setListening(t.music_id);
    };

    // Carries on to the next track with audio, like an album would.
    const listenNext = () => {
        const list = release?.tracks || [];
        const i = list.findIndex(t => t.music_id === listening);
        const next = list.slice(i + 1).find(t => trackUrl(t));
        setListening(next ? next.music_id : null);
    };

    const fetchRelease = useCallback(async (id) => {
        try {
            const { data: r } = await izenuAPI.get(`/release/fetch.php?id=${id}&include_all_tracks=1`);
            if (r.response) {
                setRelease(r.data);
            } else {
                setLoadError(true);
            }
        } catch (err) {
            console.log(err);
            setLoadError(true);
        }
    }, []);

    const handleStartEdit = useCallback(() => {
        setEditTitle(release.title || '');
        setEditType(release.type || 'album');
        setEditGenre(release.genre || '');
        setEditUpc(release.upc || '');
        setEditUpcMode(release.upc ? 'existing' : Number(release.upc_pending) === 1 ? 'izenu' : '');
        setEditReleaseDate(release.release_date || '');
        setEditStatus(release.status || 'active');
        setEditTakedownDate(release.takedown_date || '');
        setEditing(true);
    }, [release]);

    const handleSaveEdit = useCallback(async () => {
        if (!editGenre) {
            alert('Escolhe o género.');
            return;
        }
        const upcError = upcProblem(editUpcMode, editUpc, true);
        if (upcError) {
            alert(upcError);
            return;
        }
        setSaving(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/updateRelease.php', {
                release_id: parseInt(release_id),
                title: editTitle.trim(),
                type: editType,
                genre: editGenre,
                ...upcPayload(editUpcMode, editUpc),
                release_date: editReleaseDate || null,
                status: editStatus,
                takedown_date: editStatus === 'takedown' ? (editTakedownDate || null) : null
            });
            if (r.response) {
                setEditing(false);
                fetchRelease(release_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao guardar'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao guardar alterações');
        }
        setSaving(false);
    }, [release_id, editTitle, editType, editGenre, editUpc, editUpcMode, editReleaseDate, editStatus, editTakedownDate, fetchRelease]);

    const handleSaveSale = useCallback(async (payload) => {
        setSavingSale(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/updateRelease.php', {
                release_id: parseInt(release_id),
                ...payload,
            });
            if (r.response) {
                fetchRelease(release_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao guardar'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao guardar configuração de venda');
        }
        setSavingSale(false);
    }, [release_id, fetchRelease]);

    const handleToggleForSale = useCallback(() => {
        const newValue = release.for_sale ? 0 : 1;
        const payload = { for_sale: newValue };
        // If turning on for_sale and no price set, default to 1000 Kz
        if (newValue === 1 && !release.price_aoa) {
            payload.price_aoa = 1000;
        }
        handleSaveSale(payload);
    }, [release, handleSaveSale]);

    const handleChangeType = useCallback((type) => handleSaveSale({ type }), [handleSaveSale]);

    const handleMoveTrack = useCallback(async (index, direction) => {
        const tracks = [...release.tracks];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= tracks.length) return;

        [tracks[index], tracks[newIndex]] = [tracks[newIndex], tracks[index]];
        const orderedIds = tracks.map(t => t.music_id);

        // Optimistic update
        const updatedTracks = tracks.map((t, i) => ({ ...t, track_number: i + 1 }));
        setRelease(prev => ({ ...prev, tracks: updatedTracks }));

        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/reorderTracks.php', {
                release_id: parseInt(release_id),
                music_ids: orderedIds
            });
            if (!r.response) {
                fetchRelease(release_id); // Revert on error
            }
        } catch (err) {
            console.log(err);
            fetchRelease(release_id); // Revert on error
        }
    }, [release, release_id, fetchRelease]);

    const handleRemoveTrack = useCallback(async (musicId, trackTitle, linked) => {
        const here = KIND[release?.type] || 'álbum';
        if (!window.confirm(linked
            ? `Tirar "${trackTitle}" deste ${here}? Continua publicada no single de onde vem.`
            : `Remover "${trackTitle}" deste ${here}?`)) return;
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/removeTrack.php', {
                release_id: parseInt(release_id),
                music_id: musicId
            });
            if (r.response) {
                fetchRelease(release_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao remover'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao remover faixa');
        }
    }, [release, release_id, fetchRelease]);

    const fetchAvailableTracks = useCallback(async () => {
        if (!release) return;
        setLoadingTracks(true);
        try {
            const { data: r } = await izenuAPI.get(`/distributor/releases/listArtistTracks.php?artist_id=${release.artist_id}&exclude_release=${release_id}`);
            if (r.response) {
                setAvailableTracks(r.tracks);
            }
        } catch (err) {
            console.log(err);
        }
        setLoadingTracks(false);
    }, [release, release_id]);

    const handleAddTrack = useCallback(async (musicId) => {
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/addTrack.php', {
                release_id: parseInt(release_id),
                music_id: musicId
            });
            if (r.response) {
                fetchRelease(release_id);
                setAvailableTracks(prev => prev.filter(t => t.music_id !== musicId));
            } else {
                alert('Erro: ' + (r.message || 'Falha ao adicionar'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao adicionar faixa');
        }
    }, [release_id, fetchRelease]);

    const handleOpenAddTrack = useCallback(() => {
        setShowAddTrack(true);
        fetchAvailableTracks();
    }, [fetchAvailableTracks]);

    const handleArtworkChange = useCallback((e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setArtworkError('');
        if (!file) return;
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
            setArtworkError('Formato não suportado. Usa JPG, PNG ou WEBP.');
            return;
        }
        if (file.size > ARTWORK_MAX_MB * 1024 * 1024) {
            setArtworkError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${ARTWORK_MAX_MB} MB.`);
            return;
        }
        setArtworkFile(file);
        setArtworkPreview(URL.createObjectURL(file));
    }, []);

    const handleUploadArtwork = useCallback(async () => {
        if (!artworkFile || !release) return;
        setUploadingArtwork(true);
        try {
            const formData = new FormData();
            formData.append('artwork', artworkFile);
            formData.append('release_id', release_id);
            formData.append('artist_id', release.artist_id);
            const { data: r } = await izenuAPI.post('/distributor/releases/uploadArtwork.php', formData);
            if (r.response) {
                setArtworkFile(null);
                setArtworkPreview(null);
                fetchRelease(release_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao carregar capa'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao carregar capa');
        }
        setUploadingArtwork(false);
    }, [artworkFile, release, release_id, fetchRelease]);

    const handleChangeTrackState = useCallback(async (musicId, state) => {
        try {
            const { data: r } = await izenuAPI.post('/music/changeState.php', {
                music_id: musicId,
                state: state
            });
            if (r.response) {
                fetchRelease(release_id);
            } else {
                alert('Erro ao alterar estado da música');
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao alterar estado da música');
        }
    }, [release_id, fetchRelease]);

    const handleToggleActive = useCallback(async (musicId, currentActive) => {
        // The API may send active as "1"/"0"; compare as numbers.
        const newActive = Number(currentActive) === 1 ? 0 : 1;
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/toggleTrackVisibility.php', {
                music_id: musicId,
                active: newActive
            });
            if (r.response) {
                fetchRelease(release_id);
            } else {
                alert(r.message || 'Erro ao alterar visibilidade');
            }
        } catch (err) {
            console.log(err);
            alert(err?.response?.data?.message || 'Erro ao alterar visibilidade');
        }
    }, [release_id, fetchRelease]);

    const handleDeleteRelease = useCallback(async () => {
        if (!window.confirm(`Excluir este ${KIND[release?.type] || 'álbum'}?\n\nAs músicas não são apagadas: ficam na conta do artista, sem álbum associado.`)) return;
        try {
            const { data: r } = await izenuAPI.delete(`/distributor/releases/deleteRelease.php?release_id=${release_id}`);
            if (r.response) {
                navigate('/releases');
            } else {
                alert('Erro: ' + (r.message || 'Falha ao excluir'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao excluir.');
        }
    }, [release, release_id, navigate]);

    const filteredTracks = availableTracks.filter(t =>
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.singer && t.singer.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    useEffect(() => {
        fetchRelease(release_id);
    }, [release_id, fetchRelease]);

    if (loadError) {
        return (
            <div className="mx-auto max-w-6xl p-6 text-left">
                <Link to="/releases" className="text-sm text-amber-700 hover:underline">← Álbuns / EPs</Link>
                <p className="mt-6 text-gray-600">Não foi possível carregar este álbum ou single. Pode ter sido apagado, ou a ligação falhou.</p>
                <button type="button" onClick={() => { setLoadError(false); fetchRelease(release_id); }}
                    className="mt-3 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">Tentar novamente</button>
            </div>
        );
    }
    if (!release) {
        return <p className="p-10 text-center text-sm text-gray-500">A carregar…</p>;
    }

    const status = STATUS[release.status] || STATUS.draft;
    const kind = KIND[release.type] || 'álbum';
    const tracks = release.tracks || [];
    const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';
    const outlineBtn = 'rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50';
    const priceInput = (key, label, placeholder, step) => (
        <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            <input type="number" step={step} min="0" key={`${key}-${release[key]}`} defaultValue={release[key] ?? ''} placeholder={placeholder}
                onBlur={(e) => {
                    const v = e.target.value === '' ? null : parseFloat(e.target.value);
                    if (v !== (release[key] === null || release[key] === undefined ? null : Number(release[key]))) handleSaveSale({ [key]: v });
                }}
                className={inputCls} />
        </label>
    );

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-3 sm:p-6 text-left">
            <Link to="/releases" className="inline-block text-sm text-amber-700 hover:underline">← Álbuns / EPs</Link>

            {/* Header: cover + details */}
            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row">
                    <div className="mx-auto w-48 shrink-0 sm:mx-0 sm:w-56">
                        <div className="aspect-square overflow-hidden rounded-xl bg-gray-200">
                            {artworkPreview
                                ? <img src={artworkPreview} alt="" className="h-full w-full object-cover" />
                                : setCover(release.artwork, '100%')}
                        </div>
                        {artworkPreview ? (
                            <div className="mt-2 flex gap-2">
                                <button type="button" onClick={handleUploadArtwork} disabled={uploadingArtwork}
                                    className="flex-1 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                                    {uploadingArtwork ? 'A enviar…' : 'Guardar capa'}
                                </button>
                                <button type="button" onClick={() => { setArtworkFile(null); setArtworkPreview(null); }} disabled={uploadingArtwork}
                                    className={outlineBtn}>Cancelar</button>
                            </div>
                        ) : (
                            <label className={`mt-2 flex cursor-pointer items-center justify-center gap-1.5 ${outlineBtn}`}>
                                <MdCloudUpload size={16} /> {release.artwork ? 'Alterar capa' : 'Carregar capa'}
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleArtworkChange} className="sr-only" />
                            </label>
                        )}
                        <ul className="mt-2 space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                            <li><strong className="font-medium text-gray-700">Formato:</strong> JPG, PNG ou WEBP</li>
                            <li><strong className="font-medium text-gray-700">Tamanho ideal:</strong> quadrada, 3000 × 3000 px (mínimo 1400 × 1400)</li>
                            <li><strong className="font-medium text-gray-700">Peso máximo:</strong> {ARTWORK_MAX_MB} MB</li>
                        </ul>
                        {artworkError && <p className="mt-1 text-xs text-red-600">{artworkError}</p>}
                    </div>

                    <div className="min-w-0 flex-1">
                        {editing ? (
                            <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="space-y-4">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <label className="block sm:col-span-2">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Título</span>
                                        <input className={inputCls} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
                                    </label>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Tipo</span>
                                        <select className={inputCls} value={editType} onChange={(e) => setEditType(e.target.value)}>
                                            <option value="album">Álbum</option>
                                            <option value="ep">EP</option>
                                            <option value="single">Single</option>
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Género *</span>
                                        <select className={inputCls} value={editGenre} onChange={(e) => setEditGenre(e.target.value)} required>
                                            <option value="" disabled>Escolhe o género</option>
                                            {editGenre && !GENRES.some(g => g.name === editGenre) && <option value={editGenre}>{editGenre}</option>}
                                            {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
                                        <select className={inputCls} value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                                            <option value="draft">Rascunho</option>
                                            <option value="active">Activo</option>
                                            <option value="takedown">Takedown</option>
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Data de lançamento</span>
                                        <input type="date" className={inputCls} value={editReleaseDate} onChange={(e) => setEditReleaseDate(e.target.value)} />
                                    </label>
                                    <div className="sm:col-span-2">
                                        <UpcQuestion mode={editUpcMode} upc={editUpc} onModeChange={setEditUpcMode} onUpcChange={setEditUpc} showError optional />
                                    </div>
                                    {editStatus === 'takedown' && (
                                        <label className="block">
                                            <span className="mb-1 block text-sm font-medium text-gray-700">Data de takedown</span>
                                            <input type="date" className={inputCls} value={editTakedownDate} onChange={(e) => setEditTakedownDate(e.target.value)} />
                                        </label>
                                    )}
                                </div>
                                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <button type="button" onClick={() => setEditing(false)} disabled={saving} className={outlineBtn}>Cancelar</button>
                                    <button type="submit" disabled={saving || !editGenre}
                                        className="flex items-center justify-center gap-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                                        <MdCheck size={16} /> {saving ? 'A guardar…' : 'Guardar alterações'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="space-y-3 text-center sm:text-left">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{TYPE_LABELS[release.type] || release.type}</p>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{release.title}</h1>
                                    <Link to={`/artist/${release.artist_id}`} className="text-base text-amber-700 hover:underline">{release.artist_display_name || release.artist_name}</Link>
                                </div>
                                <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${status[1]}`}>{status[0]}</span>
                                    {release.genre && <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">{release.genre}</span>}
                                    {release.release_date && <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">Lançado a {release.release_date}</span>}
                                    {release.upc && <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">UPC {release.upc}</span>}
                                    {release.takedown_date && <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs text-red-800">Takedown a {release.takedown_date}</span>}
                                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">{tracks.length} {tracks.length === 1 ? 'faixa' : 'faixas'}</span>
                                </div>
                                {release.description && <p className="text-sm text-gray-600">{release.description}</p>}
                                <button type="button" onClick={handleStartEdit}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">
                                    <MdEdit size={16} /> Editar detalhes
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Store */}
            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-lg font-semibold text-gray-900">Loja</h2>
                        <p className="text-sm text-gray-500">
                            {release.for_sale
                                ? 'À venda na loja Izenu. As faixas marcadas como Exclusivas só tocam depois da compra; as Públicas tocam para todos.'
                                : `Público e grátis. Activa a venda para pôr este ${kind} na loja Izenu.`}
                        </p>
                    </div>
                    <label className="flex cursor-pointer items-center gap-3">
                        <span className={`text-sm font-semibold ${release.for_sale ? 'text-amber-700' : 'text-gray-600'}`}>{release.for_sale ? 'À venda' : 'Colocar à venda'}</span>
                        <input type="checkbox" className="peer sr-only" checked={!!release.for_sale} onChange={handleToggleForSale} disabled={savingSale} />
                        <span aria-hidden="true" className={`relative h-6 w-11 rounded-full transition ${release.for_sale ? 'bg-amber-500' : 'bg-gray-300'} peer-focus-visible:ring-2 peer-focus-visible:ring-amber-400`}>
                            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${release.for_sale ? 'left-[22px]' : 'left-0.5'}`} />
                        </span>
                    </label>
                </div>

                {!!release.for_sale && (
                    <div className="mt-4 space-y-4 border-t pt-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {priceInput('price_aoa', 'Preço (Kz)', 'Ex.: 1000')}
                            {priceInput('price_usd', 'Preço (USD) — opcional', 'Ex.: 1.99', '0.01')}
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={!!release.is_name_your_price} disabled={savingSale}
                                onChange={(e) => handleSaveSale({ is_name_your_price: e.target.checked ? 1 : 0 })}
                                className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
                            Deixar quem compra escolher o valor, com um mínimo
                        </label>
                        {!!release.is_name_your_price && (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {priceInput('min_price_aoa', 'Mínimo (Kz)', 'Ex.: 500')}
                                {priceInput('min_price_usd', 'Mínimo (USD)', 'Ex.: 0.99', '0.01')}
                            </div>
                        )}
                        <p className="text-xs text-gray-500">Os preços gravam-se sozinhos quando sais do campo.{savingSale ? ' A guardar…' : ''}</p>
                    </div>
                )}
            </section>

            {/* Tracks */}
            <section className="rounded-xl border border-gray-200 bg-white">
                <div className="border-b px-4 py-3">
                    <h2 className="text-lg font-semibold text-gray-900">Faixas{tracks.length > 0 && <span className="ms-2 text-sm font-normal text-gray-500">{tracks.length}</span>}</h2>
                    {!!release.for_sale && (
                        <p className="mt-0.5 text-xs text-gray-500"><strong className="font-medium text-gray-700">Exclusiva</strong> só toca para quem comprar o {kind}; <strong className="font-medium text-gray-700">Pública</strong> toca para todos.</p>
                    )}
                </div>

                {tracks.length > 0 ? (
                    <ul className="divide-y divide-gray-100 [&>li]:!p-0">
                        {tracks.map((e, i) => {
                            const isPublic = Number(e.active) === 1;
                            const stateCls = e.music_state === 'approved' ? 'bg-green-100 text-green-800' : e.music_state === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
                            const stateLabel = e.music_state === 'approved' ? 'Aprovada' : e.music_state === 'rejected' ? 'Rejeitada' : 'Pendente';
                            return (
                                <li key={e.music_id}>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3 sm:px-4">
                                        <div className="flex min-w-0 flex-1 basis-60 items-center gap-2 sm:gap-3">
                                            <div className="flex flex-col">
                                                <button type="button" onClick={() => handleMoveTrack(i, -1)} disabled={i === 0} aria-label="Subir"
                                                    className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowUpward size={14} /></button>
                                                <button type="button" onClick={() => handleMoveTrack(i, 1)} disabled={i === tracks.length - 1} aria-label="Descer"
                                                    className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowDownward size={14} /></button>
                                            </div>
                                            <span className="w-5 shrink-0 text-center text-sm text-gray-400">{i + 1}</span>
                                            <button type="button" onClick={() => toggleListen(e)} disabled={!trackUrl(e)}
                                                title={!trackUrl(e) ? 'Sem áudio disponível' : listening === e.music_id && !audioPaused ? 'Pausar' : 'Ouvir'}
                                                aria-label={listening === e.music_id && !audioPaused ? 'Pausar' : 'Ouvir'}
                                                className="group relative h-11 w-11 shrink-0 overflow-hidden rounded disabled:cursor-not-allowed">
                                                {setCover(e.cover || release.artwork, '100%')}
                                                {trackUrl(e) && (
                                                    <span className={`absolute inset-0 flex items-center justify-center bg-black/50 text-white transition-opacity ${listening === e.music_id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                        {listening === e.music_id && !audioPaused ? <MdPause size={22} /> : <MdPlayArrow size={22} />}
                                                    </span>
                                                )}
                                            </button>
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-gray-900">
                                                    {e.title}
                                                    {Number(e.explicit) === 1 && <span title="Conteúdo explícito" style={{ display: 'inline-block', marginLeft: 6, padding: '0 5px', borderRadius: 3, background: '#4b5563', color: '#ffffff', fontSize: 10, fontWeight: 700, lineHeight: '16px', verticalAlign: 'middle' }}>E</span>}
                                                </p>
                                                <p className="truncate text-xs text-gray-500">{e.singer} · {Number(e.plays || 0).toLocaleString('pt-PT')} plays{e.duration ? ` · ${formatDuration(e.duration)}` : ''}</p>
                                                {Number(e.linked) === 1 && (
                                                    <p className="truncate text-xs text-gray-500">
                                                        Também no {KIND[e.origin_release_type] || 'lançamento'}{' '}
                                                        <Link to={`/release/${e.origin_release_id}`} className="font-medium text-amber-700 hover:underline">{e.origin_release_title}</Link>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1.5 ps-14 sm:ps-0">
                                            <span className={`rounded px-2 py-0.5 text-xs font-semibold ${stateCls}`}>{stateLabel}</span>
                                            {e.music_state !== 'approved' && (
                                                <button type="button" onClick={() => handleChangeTrackState(e.music_id, 'approved')} title="Aprovar: a faixa passa a aparecer ao público."
                                                    className="flex items-center gap-1 rounded-full !border !border-solid border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 hover:bg-green-100"><MdCheckCircle size={13} /> Aprovar</button>
                                            )}
                                            {e.music_state !== 'rejected' && (
                                                <button type="button" onClick={() => handleChangeTrackState(e.music_id, 'rejected')} title="Reprovar: esconde a faixa do público em toda a Izenu. Continua neste lançamento."
                                                    className="flex items-center gap-1 rounded-full !border !border-solid border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-100"><MdCancel size={13} /> Reprovar</button>
                                            )}
                                            <button type="button" onClick={() => handleToggleActive(e.music_id, e.active)}
                                                disabled={isPublic && Number(e.linked) === 1}
                                                title={isPublic && Number(e.linked) === 1
                                                    ? 'Pública: esta faixa também está no single, e Exclusiva bloqueá-la-ia lá também.'
                                                    : isPublic ? 'Pública: toca para todos. Clique para tornar exclusiva de quem comprar.' : 'Exclusiva: só toca para quem comprar. Clique para tornar pública.'}
                                                className={`flex items-center disabled:cursor-not-allowed disabled:opacity-60 gap-1 rounded-full !border !border-solid px-2 py-0.5 text-xs font-medium ${isPublic ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-orange-200 bg-orange-50 text-orange-700'}`}>
                                                {isPublic ? <MdPublic size={13} /> : <MdLock size={13} />} {isPublic ? 'Pública' : 'Exclusiva'}
                                            </button>
                                            <button type="button" onClick={() => handleRemoveTrack(e.music_id, e.title, Number(e.linked) === 1)} title={`Remover deste ${kind}: a música continua na conta do artista e pode voltar a ser juntada.`} aria-label={`Remover deste ${kind}`}
                                                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><MdDelete size={18} /></button>
                                        </div>
                                    </div>
                                    {listening === e.music_id && (
                                        <div className="px-3 pb-3 sm:px-4">
                                            <audio ref={audioRef} src={trackUrl(e)} controls autoPlay className="w-full" style={{ height: 36 }}
                                                onPlay={() => setAudioPaused(false)} onPause={() => setAudioPaused(true)} onEnded={listenNext} />
                                            {!e.music_url && e.preview_url && <p className="mt-1 text-xs text-gray-500">Só a amostra de 15 s está disponível.</p>}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="px-4 py-6 text-center text-sm text-gray-500">Este {kind} ainda não tem faixas. Larga os ficheiros abaixo, ou escolhe músicas que o artista já carregou.</p>
                )}

                <ReleaseTrackUploader release={release} onUploaded={() => fetchRelease(release_id)} onChangeType={handleChangeType}
                    pickingExisting={showAddTrack}
                    onPickExisting={showAddTrack ? () => { setShowAddTrack(false); setSearchTerm(''); } : handleOpenAddTrack}>
                {showAddTrack && (
                    <div className="mt-3 rounded-lg !border !border-solid border-gray-200 bg-gray-50 p-3 text-left">
                        <div className="search-distributors-input mb-3" style={{ maxWidth: 'none' }}>
                            <input type="search" autoFocus placeholder="Pesquisar músicas do artista…" value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '8px 0', fontSize: 14 }} />
                        </div>
                        {loadingTracks ? (
                            <p className="text-sm text-gray-500">A carregar músicas…</p>
                        ) : filteredTracks.length === 0 ? (
                            <p className="text-sm text-gray-500">{searchTerm ? 'Nenhuma música encontrada.' : 'Este artista não tem outras músicas para adicionar.'}</p>
                        ) : (
                            <ul className="max-h-80 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 bg-white [&_li]:!p-0">
                                {filteredTracks.map((t) => (
                                    <li key={t.music_id}>
                                        <div className="flex items-center gap-3 px-3 py-2">
                                            <div className="h-9 w-9 shrink-0 overflow-hidden rounded">{setCover(t.cover, '100%')}</div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-gray-900">{t.title}</p>
                                                <p className="truncate text-xs text-gray-500">{t.singer}{t.release_id ? ' · noutro álbum ou single (será movida)' : ''}</p>
                                            </div>
                                            <button type="button" onClick={() => handleAddTrack(t.music_id)}
                                                className="flex shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-medium !text-white hover:bg-amber-600">
                                                <MdAdd size={14} /> Juntar
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                </ReleaseTrackUploader>
            </section>

            {/* Danger zone */}
            <section className="rounded-xl border border-red-200 bg-red-50/40 p-4 sm:p-6">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-red-800">Excluir {kind}</h2>
                        <p className="text-sm text-red-700/80">As músicas não são apagadas: ficam na conta do artista, sem álbum associado.</p>
                    </div>
                    <button type="button" onClick={handleDeleteRelease}
                        className="flex items-center gap-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium !text-white hover:bg-red-700">
                        <MdDelete size={16} /> Excluir {kind}
                    </button>
                </div>
            </section>
        </div>
    );
}
